/**
 * @file lib/signInMessage.js
 * @description The ONE owner of the sentence a failed sign-in shows.
 *
 * WHY this is its own file: the sentence has to be the SAME everywhere a
 * password is typed (the login page, the in-place "Sign in again" card), and
 * it must never say which half was wrong. Two copies of a sentence drift —
 * we have been bitten by that before — so both callers import this one.
 *
 * Paranoid-auth rule (CLAUDE.md / memory 4): a bad email and a bad password
 * get the SAME words. Nothing here may branch on "user not found" vs "wrong
 * password", because the server deliberately does not tell us which it was.
 */

/** What we say when the credentials did not work. Never "wrong password". */
export const GENERIC_SIGN_IN_FAILURE =
  "That email or password isn't right. Please try again.";

/**
 * Turn a server/network error into a sentence a person can act on, without
 * leaking which part of the credentials was wrong.
 *
 * @param {Error|{message?: string}|null} err the error thrown by auth.login
 * @returns {string} a plain-English sentence
 */
export function signInFailureMessage(err) {
  const raw = (err && err.message) || "";
  if (/temporarily locked|too many/i.test(raw)) {
    return "Too many sign-in attempts. Please wait a few minutes, then try again.";
  }
  if (/deactivated|disabled|suspended/i.test(raw)) {
    return "This account is disabled. Please contact your administrator.";
  }
  if (/invalid email or password/i.test(raw)) return GENERIC_SIGN_IN_FAILURE;
  // Anything we do not recognise (a network drop, a 500) is still not allowed
  // to describe the credentials, so an unknown server message is passed
  // through only when it plainly is not about the email or the password.
  if (/password|email address|no such user|not found/i.test(raw)) {
    return GENERIC_SIGN_IN_FAILURE;
  }
  return raw || GENERIC_SIGN_IN_FAILURE;
}

export default signInFailureMessage;
